import { HttpClient } from '@angular/common/http';
import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Random } from '@app/shared/functions/Random';
import { action, Order } from '@datorama/akita';
import * as mime from 'mime';
import { Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { PatientDocumentClient, PatientDocumentVo } from './api-client.service';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { DocumentUploadClient } from './document-upload.client';
import { PatientsService } from './patients.service';
import { AvatarsQuery } from './state/mypatientfiles/avatars.query';
import { AvatarsStore, AvatarViewModel } from './state/mypatientfiles/avatars.store';
import { DocumentsQuery } from './state/mypatientfiles/documents.query';
import { DocumentsStore } from './state/mypatientfiles/documents.store';

@Injectable({
  providedIn: 'root',
})
export class MyPatientFilesService {
  imgSrcPDF = '/assets/images/PDF.png';
  imgSrcXLS = '/assets/images/XLS.png';
  imgSrcDOC = '/assets/images/DOC.png';
  imgSrcTXT = '/assets/images/TXT.png';

  constructor(
    private documentClient: PatientDocumentClient,
    private documentUploadClient: DocumentUploadClient,

    private documentsQuery: DocumentsQuery,
    private documentsStore: DocumentsStore,

    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private configService: ConfigService,
    private httpClient: HttpClient,
    private avatarsQuery: AvatarsQuery,
    private avatarsStore: AvatarsStore,
    private patientsService: PatientsService
  ) {}

  fileUrl(tenantId: string, documentUrl: string): string {
    const accessToken = this.authService.token;
    // tslint:disable-next-line: max-line-length
    const url = `${
      this.configService.config.apiUrl
    }/api/v1/document?url=${documentUrl}&practiceId=${tenantId}&access_token=${accessToken}&r=${Random.randomNum(0, 10000)}.png`;
    return this.sanitizer.sanitize(SecurityContext.URL, url);
  }

  downloadFileBlob(url: string) {
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  // ---- Avatar in-memory cache (base64 data URL, Akita-backed) ---------------
  // Avatars were re-fetched on every navigation: `fileUrl()` stamps a random
  // cache-buster onto each URL, so the browser HTTP cache never hit and the
  // thumbnail visibly reloaded. We now download the thumbnail once, store it as
  // a base64 data URL in `AvatarsStore` (keyed by PatientId, reset on logout via
  // AuthService), and serve it from memory thereafter — stable `src`, no reload.

  /** PatientIds with an avatar download in flight, so simultaneously-mounted
   *  components don't each download the same blob. */
  private avatarLoadsInFlight = new Set<string>();

  /**
   * Patient avatar as a trusted data URL, served from the in-memory `AvatarsStore`.
   * Live store selection: a cache miss triggers a (deduped) load, and active
   * subscribers re-emit after `invalidateAvatar` + re-fetch. Emits null when the
   * patient has no avatar (cached as an empty string so a missing avatar isn't
   * re-requested on every render).
   */
  avatarDataUrlFor$(patientId: string): Observable<SafeUrl | null> {
    if (!patientId) {
      return of(null);
    }
    return this.avatarsQuery.selectEntity(patientId).pipe(
      tap(cached => {
        if (!cached) {
          this.ensureAvatarLoaded(patientId);
        }
      }),
      filter((cached): cached is AvatarViewModel => !!cached),
      map(cached => cached.AvatarUrl),
      distinctUntilChanged(),
      map(dataUrl => this.toSafeUrl(dataUrl))
    );
  }

  /** Drop a patient's cached avatar (after upload/retake/delete) so live
   *  subscribers re-download the fresh thumbnail. */
  invalidateAvatar(patientId: string): void {
    this.avatarsStore.remove(patientId);
  }

  private ensureAvatarLoaded(patientId: string): void {
    if (this.avatarLoadsInFlight.has(patientId)) {
      return;
    }
    this.avatarLoadsInFlight.add(patientId);
    this.loadAvatarDataUrl$(patientId)
      .pipe(finalize(() => this.avatarLoadsInFlight.delete(patientId)))
      .subscribe();
  }

  /** Mark the avatar data URL trusted. We built it from bytes we downloaded
   *  ourselves, so bypass Angular's URL sanitizer — which otherwise rejects the
   *  proxy's octet-stream MIME as `unsafe:` (→ ERR_UNKNOWN_URL_SCHEME). */
  private toSafeUrl(dataUrl: string | null): SafeUrl | null {
    return dataUrl ? this.sanitizer.bypassSecurityTrustUrl(dataUrl) : null;
  }

  private loadAvatarDataUrl$(patientId: string): Observable<string | null> {
    return this.authService.currentTenantId$.pipe(
      filter((tenantId): tenantId is string => !!tenantId),
      take(1),
      switchMap(tenantId =>
        this.patientsService.avatarById$(null, patientId).pipe(
          take(1),
          switchMap(doc => (doc ? of(doc) : this.patientsService.loadPatientAvatar(tenantId, patientId).pipe(catchError(() => of(null))))),
          switchMap(doc => {
            const thumbnailUrl = doc && (doc as any).DocumentUrl ? (doc as any).ThumbnailUrl : null;
            if (!thumbnailUrl) {
              this.cacheAvatarDataUrl(patientId, '');
              return of<string | null>(null);
            }
            return this.downloadFileBlob(this.fileUrl(tenantId, thumbnailUrl)).pipe(
              switchMap(blob => this.blobToDataUrl(blob)),
              tap(dataUrl => this.cacheAvatarDataUrl(patientId, dataUrl)),
              catchError(() => of<string | null>(null))
            );
          })
        )
      )
    );
  }

  private cacheAvatarDataUrl(patientId: string, avatarUrl: string): void {
    this.avatarsStore.upsert(patientId, { PatientId: patientId, AvatarUrl: avatarUrl } as AvatarViewModel);
  }

  private blobToDataUrl(blob: Blob): Observable<string> {
    return new Observable<string>(observer => {
      const reader = new FileReader();
      reader.onloadend = () => {
        observer.next(reader.result as string);
        observer.complete();
      };
      reader.onerror = () => observer.error(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  getThumbnail$(thumbnailAddress: string, documentUrl: string, params: any): Observable<string> {
    const fileType = this.fileType(documentUrl);
    if (fileType === 'application/xls' || fileType === 'text/csv') {
      return of(this.imgSrcXLS);
    } else if (fileType === 'application/doc') {
      return of(this.imgSrcDOC);
    } else if (fileType === 'image/png' || fileType === 'image/jpeg' || fileType === 'image/jpg' || fileType === 'application/pdf') {
      return of(this.fileUrl(params.tenantId, thumbnailAddress));
    } else {
      return of(this.imgSrcTXT);
    }
  }

  fileType(filename: string): string {
    return mime.getType(filename);
  }

  patientDocuments$(tenantId: string, patientId: string) {
    return this.documentsQuery.selectAll({
      filterBy: entity => entity.PracticeId === tenantId && entity.PatientId === patientId,
      sortBy: 'DocumentDate',
      sortByOrder: Order.DESC,
    });
  }

  practiceDocuments$(tenantId: string) {
    return this.documentsQuery.selectAll({
      filterBy: entity => entity.PracticeId === tenantId && entity.PatientId === null,
      sortBy: 'DocumentDate',
      sortByOrder: Order.DESC,
    });
  }

  /*
  getPatientDocuments(practiceid: string, patientId: string): Observable<PatientDocumentVo[]>
  getPatientDocumentAvatar(practiceid: string, patientId: string): Observable<PatientDocumentVo>
  getPatientDocument(practiceid: string, documentId: string): Observable<PatientDocumentVo>
  getPatientDocumentHistory(practiceid: string, documentId: string): Observable<PatientDocumentHistoryVo>

  doctor documents => getClinicalDocuments(practiceid: string, filter: string): Observable<PatientDocumentVo[]>
  */

  private avatarViewModel(documentVo: PatientDocumentVo) {
    return {
      PracticeId: documentVo.PracticeId,
      PatientId: documentVo.PatientId,
      AvatarUrl: documentVo.ThumbnailUrl,
      LastUpdated: new Date(),
    } as AvatarViewModel;
  }

  @action('getDocumentsForPatient')
  public getDocumentsForPatient(tenantId: string, patientId: string) {
    const request = this.documentClient.getPatientDocuments(tenantId, patientId);
    return request.pipe(
      tap(document => {
        if (!!document) {
          this.documentsStore.add(document);
        }
      })
    );
  }

  @action('getDocumentsForPractice')
  public getDocumentsForPractice(tenantId: string, filter: string) {
    const request = this.documentClient.getClinicalDocuments(tenantId, filter);
    return request.pipe(
      tap(document => {
        if (!!document) {
          this.documentsStore.add(document);
        }
      })
    );
  }

  @action('uploadDocument')
  uploadDocument(tenantId: string, patientId: string, documentVo: PatientDocumentVo, file: Blob) {
    const request = this.documentUploadClient.capturePatientDocument(tenantId, patientId, documentVo, file, null);
    return request.pipe(
      tap(document => {
        if (!!document) {
          // this.documentsStore.add(document);
        }
      })
    );
  }

  @action('uploadImages')
  uploadImages(tenantId: string, patientId: string, documentVo: PatientDocumentVo, pictures: string[]) {
    const request = this.documentUploadClient.capturePatientDocument(tenantId, patientId, documentVo, null, pictures);
    return request.pipe(
      tap(document => {
        if (!!document) {
          // this.documentsStore.add(document);
        }
      })
    );
  }

  getLastDocumentByCategory(tenantId: string, patientId: string, category: string) {
    const request = this.documentClient.getPatientDocumentByCategory(tenantId, patientId, category);
    return request;
  }

  removeDocument(tenantId: string, documentId: string) {
    const request = this.documentClient.removeDocument(tenantId, documentId);
    return request;
  }
}
